import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  OrganizationIntegrationSubstrateItem,
  OrganizationIntegrationSubstrateItemCreate,
  OrganizationIntegrationSubstrateItemUpdate,
} from "../../common/types";
import {
  useCreateOrganizationIntegrationSubstrateItem,
  useDeleteOrganizationIntegrationSubstrateItem,
  useListOrganizationIntegrationSubstrateItems,
  useUpdateOrganizationIntegrationSubstrateItem,
} from "../../services/api";

interface SharePointSitesManagerProps {
  open: boolean;
  organizationId: string;
  organizationIntegrationId: string;
  onClose: () => void;
}

interface SiteDraft {
  siteUrl: string;
  displayLabel: string;
}

const emptyDraft: SiteDraft = {
  siteUrl: "",
  displayLabel: "",
};

const readSiteDraft = (
  item?: OrganizationIntegrationSubstrateItem | null,
): SiteDraft => ({
  siteUrl: String(item?.config?.site_url || ""),
  displayLabel: String(item?.display_label || ""),
});

const SharePointSitesManager: React.FC<SharePointSitesManagerProps> = ({
  open,
  organizationId,
  organizationIntegrationId,
  onClose,
}) => {
  const listItems = useListOrganizationIntegrationSubstrateItems();
  const createItem = useCreateOrganizationIntegrationSubstrateItem();
  const updateItem = useUpdateOrganizationIntegrationSubstrateItem();
  const deleteItem = useDeleteOrganizationIntegrationSubstrateItem();
  const [items, setItems] = useState<OrganizationIntegrationSubstrateItem[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] =
    useState<OrganizationIntegrationSubstrateItem | null>(null);
  const [draft, setDraft] = useState<SiteDraft>(emptyDraft);
  const [deleteTarget, setDeleteTarget] =
    useState<OrganizationIntegrationSubstrateItem | null>(null);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await listItems(organizationId, organizationIntegrationId));
    } catch (requestError: any) {
      setError(
        requestError?.message || "Failed to load SharePoint substrate items.",
      );
    } finally {
      setLoading(false);
    }
  }, [listItems, organizationId, organizationIntegrationId]);

  useEffect(() => {
    if (!open) return;
    loadItems();
  }, [loadItems, open]);

  const openAdd = () => {
    setEditingItem(null);
    setDraft(emptyDraft);
    setEditorOpen(true);
  };

  const openEdit = (item: OrganizationIntegrationSubstrateItem) => {
    setEditingItem(item);
    setDraft(readSiteDraft(item));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingItem(null);
    setDraft(emptyDraft);
  };

  const savingDisabled = useMemo(
    () => saving || draft.siteUrl.trim() === "",
    [draft.siteUrl, saving],
  );

  const handleSave = async () => {
    const basePayload = {
      display_label: draft.displayLabel.trim() || null,
      config: {
        site_url: draft.siteUrl.trim(),
      },
    };

    try {
      setSaving(true);
      if (editingItem) {
        const payload: OrganizationIntegrationSubstrateItemUpdate = basePayload;
        await updateItem(
          organizationId,
          organizationIntegrationId,
          editingItem.id,
          payload,
        );
      } else {
        const payload: OrganizationIntegrationSubstrateItemCreate = basePayload;
        await createItem(organizationId, organizationIntegrationId, payload);
      }
      await loadItems();
      closeEditor();
    } catch (saveError: any) {
      setError(saveError?.message || "Failed to save SharePoint site.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await deleteItem(
        organizationId,
        organizationIntegrationId,
        deleteTarget.id,
      );
      await loadItems();
      setDeleteTarget(null);
    } catch (deleteError: any) {
      setError(deleteError?.message || "Failed to delete SharePoint site.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog sx={{ width: "min(960px, calc(100vw - 32px))" }}>
          <DialogTitle>Manage SharePoint sites</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {error && (
                <Alert color="danger" variant="soft">
                  {error}
                </Alert>
              )}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={1}
              >
                <Typography level="body-sm" color="neutral">
                  Add, rename, and remove the SharePoint sites that workspaces
                  can select.
                </Typography>
                <Button
                  size="sm"
                  variant="solid"
                  startDecorator={<AddIcon />}
                  onClick={openAdd}
                >
                  Add another site
                </Button>
              </Stack>

              <Sheet
                variant="outlined"
                sx={{ borderRadius: "8px", overflowX: "auto" }}
              >
                <Table hoverRow sx={{ minWidth: 720 }}>
                  <thead>
                    <tr>
                      <th style={{ width: "45%" }}>Site URL</th>
                      <th style={{ width: "20%" }}>Label</th>
                      <th style={{ width: "20%" }}>Last modified</th>
                      <th style={{ width: "15%", textAlign: "right" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.config?.site_url || "Unknown site"}</td>
                        <td>{item.display_label || "—"}</td>
                        <td>{item.updated_at || "—"}</td>
                        <td style={{ textAlign: "right" }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              startDecorator={<EditIcon />}
                              onClick={() => openEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="danger"
                              startDecorator={<DeleteIcon />}
                              onClick={() => setDeleteTarget(item)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </td>
                      </tr>
                    ))}
                    {!loading && items.length === 0 && (
                      <tr>
                        <td colSpan={4}>
                          <Typography level="body-sm" color="neutral">
                            No SharePoint sites configured yet.
                          </Typography>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Sheet>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={editorOpen} onClose={closeEditor}>
        <ModalDialog sx={{ width: "min(520px, calc(100vw - 32px))" }}>
          <DialogTitle>
            {editingItem ? "Edit SharePoint site" : "Add SharePoint site"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Input
                value={draft.siteUrl}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    siteUrl: event.target.value,
                  }))
                }
                placeholder="https://contoso.sharepoint.com/sites/board"
              />
              <Input
                value={draft.displayLabel}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    displayLabel: event.target.value,
                  }))
                }
                placeholder="Optional label"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={savingDisabled}
            >
              Save
            </Button>
            <Button variant="plain" color="neutral" onClick={closeEditor}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <ModalDialog sx={{ width: "min(480px, calc(100vw - 32px))" }}>
          <DialogTitle>Delete SharePoint site</DialogTitle>
          <DialogContent>
            <Typography level="body-sm">
              Remove{" "}
              {deleteTarget?.display_label ||
                deleteTarget?.config?.site_url ||
                "this SharePoint site"}
              ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="danger" onClick={handleDelete} loading={saving}>
              Delete
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default SharePointSitesManager;
